import { webConfig } from '@gitbook/env';

import type { FileLocation } from './types';

const FIREBASE_STORAGE_HOST = 'firebasestorage.googleapis.com';
const GOOGLE_STORAGE_HOST = 'storage.googleapis.com';
export const EMULATOR_STORAGE_HOST = process.env.FIREBASE_STORAGE_EMULATOR_HOST;

const VALID_STORAGE_HOSTS = [
    FIREBASE_STORAGE_HOST,
    GOOGLE_STORAGE_HOST,
    EMULATOR_STORAGE_HOST,
].filter(Boolean);

/**
 * Rwrites a legacy downloadURL using the old legacy bucket deleted on GCP by replacing
 * the bucket with the working legacy bucket.
 * /!\ For simplicity and because we don't want to use `app-database` in `@services/hive`,
 * /!\ this function has been copied in services/hive/hive/src/duplicate/duplicateContentRevision.ts
 * /!\ If you change this function, you should also change the one in `@services/hive`.
 */
export function rewriteLegacyBucketDownloadURL(downloadURL: string): string {
    if (!webConfig.storage.legacyFilesPreviousBucket) {
        return downloadURL;
    }

    if (!webConfig.storage.legacyFilesBucket) {
        throw new Error(
            'Tried to use a legacy file bucket from a previous project but no legacy bucket was configured in the current project'
        );
    }

    // Replace the old storage bucket from the download URL, if it exists
    return downloadURL.replace(
        `/b/${webConfig.storage.legacyFilesPreviousBucket}/o`,
        `/b/${webConfig.storage.legacyFilesBucket}/o`
    );
}

/**
 * Return the appropriate bucket for the given file location.
 */
export function getBucketNameFromLocation(_location: FileLocation): string {
    return webConfig.firebase.storageBucket;
}

/**
 * Validates if a download URL is from one a known host storage
 */
export function isStorageURL(downloadURL: string): boolean {
    const url = new URL(downloadURL);

    return VALID_STORAGE_HOSTS.includes(url.host);
}

/**
 * Validates if a download URL is from one a known host storage and that it points to a specific bucket for a given location
 */
export function isStorageURLForLocation(downloadURL: string, location: FileLocation): boolean {
    try {
        return getBucketNameFromDownloadURL(downloadURL) === getBucketNameFromLocation(location);
    } catch {
        return false;
    }
}

/**
 * Parse a url from GCP / Firebase storage.
 */
export function parseStorageDownloadURL(downloadURL: string): { bucket: string; path: string } {
    const re = /(?:\/download\/storage)?\/v\d\/b\/([^/]+)\/o\/([^/]*)/;
    const url = new URL(downloadURL);

    if (!isStorageURL(downloadURL)) {
        throw new Error(`Invalid storage hostname "${url.hostname}"`);
    }

    const match = url.pathname.match(re);
    if (!match) {
        throw new Error(`Could not parse ${downloadURL} as a storage url`);
    }

    let bucket = match[1];

    if (bucket === webConfig.storage.legacyFilesPreviousBucket) {
        if (!webConfig.storage.legacyFilesBucket) {
            throw new Error(
                'Tried to use a legacy file bucket from a previous project but no legacy bucket was configured in the current project'
            );
        }

        bucket = webConfig.storage.legacyFilesBucket;
    }

    return {
        bucket,
        path: decodeURIComponent(match[2]),
    };
}

/**
 * Returns the name of a bucket given a download URL
 */
export function getBucketNameFromDownloadURL(downloadURL: string): string {
    return parseStorageDownloadURL(downloadURL).bucket;
}

/**
 * Extract the path of a file in a bucket given its download url.
 */
export function getFilePathFromDownloadURL(downloadURL: string): string {
    return parseStorageDownloadURL(downloadURL).path;
}
